import type { IObservableArray } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import type { BaseCampaign, Recommendation, TTDCampaign } from '@feathr/blackbox';
import CampaignPage from '@feathr/extender/App/EventsPage/CampaignPage';
import ReportActions from '@feathr/extender/components/PageHeaderActions';
import { useReportExport } from '@feathr/extender/hooks';
import { useStore } from '@feathr/extender/state';

import CampaignReportSection, { getCampaignReportConfig } from '../CampaignReportSection';
import CampaignRecommendations from './CampaignRecommendations';
import useRecommendations from './DefaultCampaignPage.useRecommendations';

import * as styles from './DefaultCampaignPage.css';

/**
 * Default campaign report page component used for Tradedesk, Facebook and Google campaigns: Affinity, Email Mapping, Lookalike, Historical Geofencing, Mobile Geofencing, Retargeting, Search, Facebook Email Mapping, Facebook Retargeting and Google Ads Grants
 *
 * @returns The default campaign page component
 */
function DefaultCampaignPage(): JSX.Element {
  const { t } = useTranslation();
  const { Campaigns } = useStore();
  const { campaignId } = useParams<{ eventId: string; campaignId: string }>();

  const campaign: BaseCampaign = Campaigns.get(campaignId);
  const config = getCampaignReportConfig(campaign);
  const reportExportProps = useReportExport({ model: campaign, config });
  const {
    recommendations,
    hasActiveRecommendationsOnLoad,
    hasRecommendationsDismissed,
    onMarkRecommendationDismissed,
    showRecommendations,
    onRefreshCollection,
    onDismissRefreshCollection,
  } = useRecommendations({
    campaign,
  });

  const actions = <ReportActions campaign={campaign} {...reportExportProps} />;

  return (
    <CampaignPage
      actions={actions}
      campaign={campaign}
      className={styles.root}
      title={campaign.name}
    >
      {showRecommendations && (
        <CampaignRecommendations
          campaign={campaign as TTDCampaign}
          hasActiveRecommendationsOnLoad={hasActiveRecommendationsOnLoad}
          hasRecommendationsDismissed={hasRecommendationsDismissed}
          onDismissRefreshCollection={onDismissRefreshCollection}
          onMarkRecommendationDismissed={onMarkRecommendationDismissed}
          onRefreshCollection={onRefreshCollection}
          recommendations={recommendations?.models as IObservableArray<Recommendation>}
        />
      )}
      <h3>{t('Report')}</h3>
      <CampaignReportSection campaign={campaign} {...reportExportProps} />
    </CampaignPage>
  );
}

export default observer(DefaultCampaignPage);
